import React, { useEffect, useState } from "react";
import { Table, Popconfirm, Space } from "antd";
import { v4 as uuidv4 } from "uuid";
import {
  Button,
  Modal,
  TextField,
  Avatar,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  DeleteOutlined,
  EditOutlined,
  FileAddTwoTone,
} from "@ant-design/icons";
import { ScheduleMessageApiService } from "../../services/api/ScheduleMessageAPIService";
import {
  API_ERROR_MESSAGE,
  API_SUCCESS_MESSAGE,
  BUTTON,
  TABLE_HEADING,
} from "../../shared/constants";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calculateDueDate } from "../../shared/utils";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function TodoListUpdates() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackData, setSnackData] = useState({
    show: false,
    message: "",
    type: "error",
  });
  const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 100,
    });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [form, setForm] = useState({ day: "", day_of_week: "", tasks: "" });
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    fetchMessageDetails();
  }, []);

  const fetchMessageDetails = () => {
    setLoading(true);
    ScheduleMessageApiService.messageListing("todo_list_updates")
      .then((response) => {
        setData(response);
      })
      .catch((errResponse) => {
        setSnackData({
          show: true,
          message:
            errResponse?.response?.data?.message ||
            API_ERROR_MESSAGE.INTERNAL_SERVER_ERROR,
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleAddNew = () => {
    setIsEdit(false);
    setForm({ day: "", day_of_week: "", tasks: "" });
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setIsEdit(true);
    setForm({ ...record });
    setIsModalOpen(true);
  };

  const handleDelete = (record) => {
    console.log("record", record);
    ScheduleMessageApiService.messageDelete(
      "todo_list_updates",
      record.id,
      record.day
    )
      .then(() => {
        setSnackData({
          show: true,
          message: "Deleted successfully!",
          type: "success",
        });
        fetchMessageDetails();
      })
      .catch(() =>
        setSnackData({
          show: true,
          message: "Failed to delete",
          type: "error",
        })
      );
  };

  const handleSave = () => {
    let payload = {};

    if (isEdit) {
      payload = {
        day_of_week: form.day_of_week,
        tasks: form.tasks,
      };
    } else {
      payload = {
        id: uuidv4().replace(/-/g, ""),
        day: +form.day,
        day_of_week: form.day_of_week,
        tasks: form.tasks,
      };
    }

    const apiCall = isEdit
      ? ScheduleMessageApiService.messageEdit(
          "todo_list_updates",
          payload,
          form.id,
          form.day,
          ""
        )
      : ScheduleMessageApiService.messageCreation(
          "todo_list_updates",
          payload,
          ""
        );

    apiCall
      .then(() => {
        setSnackData({
          show: true,
          message: isEdit ? "Updated successfully!" : "Created successfully!",
          type: "success",
        });
        fetchMessageDetails();
        setIsModalOpen(false);
      })
      .catch(() =>
        setSnackData({
          show: true,
          message: "API Error!",
          type: "error",
        })
      );
  };

  const columns = [
    {
        title: TABLE_HEADING.DAY_OF_PREGNANCY,
        dataIndex: 'day',
        width:"15%",
    },
    {
        title: TABLE_HEADING.DAY,
        dataIndex: 'day_of_week',
        width:"10%",
    },
    {
      title: "Due Date By",
      dataIndex: "due_date",
      width: "10%",
      render: (_, record) => calculateDueDate(record.day),
    },
    {
      title: TABLE_HEADING.MESSAGE,
      dataIndex: "tasks",
      render: (tasks) => (
        tasks?.trim().split('\n').map((line, index) =>
          line ? (
            <div
              key={index}
              dangerouslySetInnerHTML={{ __html: line }}
            />
          ) : (<br/>)
        )
      )
    },
    {
      title: TABLE_HEADING.ACTION,
      key: "action",
      render: (record) => (
        <Space>
          <Button
            onClick={() => handleEdit(record)}
            startIcon={<EditOutlined />}
          >
            {BUTTON.EDIT}
          </Button>
          <Popconfirm
            title="Are you sure to delete this message?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button startIcon={<DeleteOutlined style={{ color: "red" }} />}>
              {" "}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: "blue", width: 48, height: 48, fontSize: 20 }}>
            T
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            Todo List
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileAddTwoTone />}
          onClick={handleAddNew}
        >
          Add New
        </Button>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Table
          rowKey="day"
          className="custom-table"
          columns={columns}
          dataSource={data}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '50', '100', '200'],
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
              window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top on page change
            },
          }}
        />
      )}

      {/* Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isEdit ? "Update" : "Add new"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} p={2}>
            <TextField
              label="Day"
              value={form.day}
              onChange={(e) => setForm({ ...form, day: e.target.value })}
              type="number"
              fullWidth
            />

            <TextField
              label="Day of Week"
              value={form.day_of_week}
              onChange={(e) =>
                setForm({ ...form, day_of_week: e.target.value })
              }
              type = "text"
              fullWidth
            />
            <TextField
              label="Tasks"
              value={form.tasks}
              multiline
              minRows={4}
              onChange={(e) => setForm({ ...form, tasks: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
            <Button variant="outlined" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              {isEdit ? "Update" : "Create"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        style={{ top: "80px" }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackData.show}
        autoHideDuration={3000}
        onClose={() => setSnackData({ show: false })}
      >
        <Alert
          severity={snackData.type}
          onClose={() => setSnackData({ show: false })}
        >
          {snackData.message}
        </Alert>
      </Snackbar>
    </>
  );
}
